import { SiteKind, SyncCoreDataDefinitionsEnumTranslator } from '@edgebox/sync-core-data-definitions';
import { ClientSiteEntity } from '@edgebox/sync-core-rest-client';

/**
 * The part of a site that says what it is. Sync Core keeps one site per project and application type
 * as the source of the platform's own terms, and marks it with a kind; the sites a customer connected
 * are of kind {@link SiteKind.Site}. A site stored before kinds existed carries none, which reads as a
 * regular site.
 */
export type SiteWithKind = Pick<ClientSiteEntity, 'kind'>;

export function siteKindOf(site: SiteWithKind): SiteKind {
  return site.kind ?? SiteKind.Site;
}

/**
 * A site nobody connected and nobody administers: it belongs to the platform, it is the origin of
 * terms other sites receive, and it is offered in none of the places that list, pick or count the
 * sites of a project.
 */
export function isPlatformGovernanceSite(site: SiteWithKind): boolean {
  return siteKindOf(site) === SiteKind.PlatformGovernance;
}

/** The sites a screen may offer: everything a customer connected, in the order it was given. */
export function onlyRegularSites<SiteType extends SiteWithKind>(sites: SiteType[]): SiteType[] {
  return sites.filter((site) => !isPlatformGovernanceSite(site));
}

/**
 * The site a filter may show as chosen. A site reached by id or uuid — from a link, a stored filter,
 * a host page — is only a choice when it is one of the sites a screen offers.
 */
export function selectableSite<SiteType extends SiteWithKind>(site: SiteType | undefined): SiteType | undefined {
  return site && isPlatformGovernanceSite(site) ? undefined : site;
}

/**
 * What a reader is shown in place of a site name. The wording belongs to the data definitions, so a
 * screen never spells out a kind itself and every surface says the same thing.
 */
export function siteKindLabel(site: SiteWithKind): string {
  return SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue<SiteKind>('SiteKind', siteKindOf(site));
}

/**
 * What a reader is shown for a site a reference names but this session cannot read — a site that is
 * gone, or one the engine does not hand out. The reference stays on the screen either way, so it says
 * that much rather than waiting, and it never borrows the look of a site that is there.
 */
export const UNRESOLVED_SITE_LABEL = 'Unknown site';
